import { defineStore } from 'pinia';
import type { GetUserDataResponse } from '@/shared/DTO/CX';
import { ROLES } from '@/shared/constants/roles';
import { computed, ref } from 'vue';

export const useUserStore = defineStore('users', () => {
    const currentUser = ref<GetUserDataResponse>({} as GetUserDataResponse);
    const originalUser = ref<GetUserDataResponse | null>(null);

    function setCurrentUser(user: GetUserDataResponse) {
        currentUser.value = user;
    }

    function reset() {
        currentUser.value = {} as GetUserDataResponse;
        originalUser.value = null;
    }

    function setOriginalUser() {
        originalUser.value = currentUser.value;
    }

    function stopImpersonation() {
        if (originalUser.value) {
            currentUser.value = originalUser.value;
            originalUser.value = null;
        }
    }

    const isSuperAdmin = computed(() => currentUser.value.role === ROLES.SUPER_ADMIN);
    const isPortfolioAdmin = computed(() => currentUser.value.role === ROLES.PORTFOLIO_ADMIN);
    const isAdmin = computed(() => currentUser.value.role === ROLES.ADMIN);
    const isProjectManager = computed(() => currentUser.value.role === ROLES.PROJECT_MANAGER);
    const isUser = computed(() => currentUser.value.role === ROLES.USER);
    const isImpersonating = computed(() => originalUser.value !== null);

    /**
     * Get IDs of workspaces the user is responsible for.
     * Returns empty array if user is not a workspace manager.
     */
    const managedWorkspaceIds = computed(() => {
        // Admins and super-admins have access to all workspaces (empty array means all)
        if (isAdmin.value || isSuperAdmin.value) {
            return [];
        }

        // Return the managed workspace IDs from the API response
        return currentUser.value.managedWorkspaceIds ?? [];
    });

    /**
     * Check if the user is a workspace manager (responsable d'espace).
     * Uses the managedWorkspaceIds array from GetUserDataResponse.
     * Admins and super-admins are also considered workspace managers for compatibility.
     */
    const isWorkspaceManager = computed(() => {
        // If user is admin or super-admin, they are considered workspace managers
        if (isAdmin.value || isSuperAdmin.value) {
            return true;
        }

        // Check if user has at least one managed workspace
        return (managedWorkspaceIds.value?.length ?? 0) > 0;
    });

    return {
        currentUser,
        originalUser,
        setCurrentUser,
        reset,
        setOriginalUser,
        stopImpersonation,
        isSuperAdmin,
        isPortfolioAdmin,
        isAdmin,
        isProjectManager,
        isUser,
        isImpersonating,
        isWorkspaceManager,
        managedWorkspaceIds,
    };
}, {
    persist: true,
});

export type UserStore = ReturnType<typeof useUserStore>;
